<script setup lang="ts">
import { addHours, parse, format } from 'date-fns'

// Idioma
const { locale, t } = useI18n()
var browserLang = navigator?.language ?? locale.value

// Props
const props = defineProps({
  data: Object,
  arrayHoras: Object,
  urlParams: String,
  alternative: Boolean
})

// Promo
const uniquePromos = computed(() => Array.from(new Set((props.data?.promo ?? []).map((p: Promo) => p.description))))

// Imagenees
const images = computed(() => props.data?.images)

// Hover event
const hoverCarousel = ref(false)
const hoverCard = ref(false)

const emit = defineEmits(['hoverHotel'])
watch(hoverCard, (newValue) => {
  if (newValue) {
    emit('hoverHotel', props.data?.id)
  } else {
    emit('hoverHotel', 0)
  }
})

const deviceStore = useDeviceStore();
const isMobile = deviceStore.isMobile;

const openModalPromo = ref(false)
const openModalPiscina = ref(false)

const sumarHoras = (hora: string, cantidad: number): string => {
  // Detecta si es formato 12h con AM/PM
  const is12h = /AM|PM/i.test(hora)
  let date
  if (is12h) {
    date = parse(hora, 'hh:mm a', new Date())
  } else {
    date = parse(hora, 'HH:mm', new Date())
  }
  const nueva = addHours(date, cantidad)
  return is12h ? format(nueva, 'hh:mm a') : format(nueva, 'HH:mm')
}

const horasScroll = ref<HTMLElement | null>(null)

const open = ref(false)
const activeIndex = ref(0)
function onSelect(index: number) {
  activeIndex.value = index
}

// Computed property for available hours
const availableHours = computed(() => {
  return [3, 6, 24].filter(hour => props.arrayHoras?.[hour]?.length)
})

const { loggedIn, user } = useUserSession()
const userPending = computed(() => {
  return (user.value as OAuthUser)?.status?.id === 2
})

// Favorites Store
const favoritesStore = useFavoritesStore()
const { favoriteHotel: storeFavoriteHotel, unfavoriteHotel: storeUnfavoriteHotel } = favoritesStore
const { favoriteHotels, invalidateSearchCache } = storeToRefs(favoritesStore)

// Estado local del favorito
const favorite = computed(() => {
  return favoriteHotels.value.has(props?.data?.id)
})

// Modales de login y registro
const showLoginModal = ref(false)
const showRegisterModal = ref(false)

// Sincronizar estado inicial
onMounted(() => {
  if (props?.data?.favorite) {
    favoritesStore.addFavorite(props?.data?.id)
  }
})

// Función para marcar como favorito usando el store
const favoriteHotel = async (hotelId: number) => {
  await storeFavoriteHotel(hotelId)
}

// Función para desmarcar como favorito usando el store
const unfavoriteHotel = async (hotelId: number) => {
  await storeUnfavoriteHotel(hotelId)
}

const toast = useToast()

</script>

<template>
  <div class="relative h-full gap-4 sm:gap-3 flex flex-col sm:flex-row rounded-2xl" @mouseover="hoverCard = true"
    @mouseleave="hoverCard = false">

    <div :class="[
      'relative sm:w-5/12 lg:w-4/12 2xl:w-5/12 rounded-2xl bg-neutral-100 shrink-0',
      hoverCard
        ? 'shadow-[0px_0px_20px_0_rgba(0,0,0,0.1)]'
        : 'shadow-[0px_0px_10px_0_rgba(0,0,0,0.1)]'
    ]" :style="{
        transition: 'box-shadow 0.3s cubic-bezier(0.4,0,0.2,1)'
      }">
      <!-- Carousel -->
      <UCarousel :arrows="!isMobile && hoverCarousel ? true : false" @mouseover="hoverCarousel = true"
        @mouseleave="hoverCarousel = false" v-slot="{ item }" :items="images" :class="`h-full sm:min-h-44`"
        :next="{ size: 'xs', variant: 'soft' }" :prev="{ size: 'xs', variant: 'soft' }" :ui="{
          item: 'basis-full h-full',
          container: 'h-full',
          viewport: 'aspect-16/9 w-full h-full rounded-2xl',
          next: 'sm:end-2',
          prev: 'sm:start-2'
        }" @select="onSelect">
        <NuxtImg provider="byhours" format="webp" width="600" height="400" :src="generateImgUrl(String(item ?? ''))"
          @click="navigateTo(`/${locale}/${props?.data?.slug}-${props?.data?.id}.html?${urlParams ?? ''}`, { open: { target: alternative ? '_self' : '_blank' } })"
          :alt="`Imagen hotel`" class="w-full h-full object-cover cursor-pointer rounded-2xl" draggable="false"
          loading="lazy" />
      </UCarousel>

      <!-- Indicador de posición -->
      <div v-if="isMobile && images?.length > 1" class="absolute bottom-2 right-2 z-10">
        <div class="bg-neutral-900/70 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
          {{ activeIndex + 1 }}/{{ images?.length }}
        </div>
      </div>

      <!-- Horas disponibles -->
      <div class="absolute top-0 left-0 p-2 z-10 flex justify-between w-full">
        <UModal v-model:open="open" :ui="{ content: 'max-w-2xl max-h-[80vh] overflow-y-auto' }"
          :title="$t('labels.horasDisponibles')">
          <UButton :label="$t('buttons.dispo')" color="neutral" variant="soft" size="xs" icon="i-lucide-clock"
            @click.stop.prevent="open = true" class="shrink-0" :ui="{
              base: 'drop-shadow-md'
            }" />
          <template #body>
            <div class="text-xs">
              <div v-for="key in availableHours" :key="key">
                <div v-if="arrayHoras?.[key]?.length" class="pb-6">
                  <div class="font-bold mb-2">{{ key }}h</div>
                  <div class="flex flex-wrap gap-2 sm:gap-3">
                    <div v-for="item in arrayHoras?.[key]">
                      <UButton :label="arrayHoras?.[key]?.length === 1 ? item : `${item} - ${sumarHoras(item, key)}`"
                        color="neutral" variant="outline" size="xs" @click="() => {
                          navigateTo(`/${locale}/${props?.data?.slug}-${props?.data?.id}.html?${urlParams ?? ''}&pack=${key}&hora=${item}`, { open: { target: alternative ? '_self' : '_blank' } })
                        }" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </UModal>
      </div>

      <!-- Botón de favorito -->
      <div class="absolute top-0 right-0 p-2 z-10">
        <UButton :aria-label="$t('labels.aria.favorito')" :icon="loggedIn && favorite && !userPending ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'"
          color="neutral" size="sm" variant="soft" class="hover:scale-105 transition-all duration-300" :ui="{
            base: 'drop-shadow-md',
            leadingIcon: loggedIn && favorite && !userPending ? 'text-error' : 'text-neutral-900'
          }" @click="() => {
            if (loggedIn && !userPending) {
              if (!favorite) {
                favoriteHotel(props?.data?.id)
              } else {
                unfavoriteHotel(props?.data?.id)
              }
            } else if (userPending) {
              toast.add({
                title: t('labels.user_zone.menu.favoritos'),
                description: t('labels.user_zone.favoritos.completeProfile'),
                color: 'warning',
                icon: 'i-heroicons-heart',
                actions: [{
                  icon: 'i-lucide-user-circle',
                  label: t('labels.user_zone.menu.perfil'),
                  color: 'neutral',
                  variant: 'subtle',
                  onClick: (e) => {
                    navigateTo(`/${locale}/user-zone/profile`)
                  }
                }]
              })
            } else {
              showLoginModal = true
            }
          }" />
      </div>

    </div>

    <transition name="fade-card" mode="out-in" :duration="300">
      <NuxtLink :to="`/${locale}/${props?.data?.slug}-${props?.data?.id}.html?${urlParams ?? ''}`"
        :target="alternative ? '_self' : '_blank'" :class="[
          'bg-transparent px-0 sm:bg-white sm:p-5',
          'transition-all duration-300 w-full sm:w-7/12 lg:w-8/12 flex flex-col sm:justify-between rounded-2xl',
          'shadow-none',
          !isMobile ? 'hover:shadow-[0px_0px_20px_0_rgba(0,0,0,0.1)]' : 'hover:shadow-none',
          'sm:shadow-[0px_0px_10px_0_rgba(0,0,0,0.1)]'
        ]" :aria-label="props?.data?.name">
        <div class="flex flex-col gap-0.5 min-w-0 sm:flex-1">

          <!-- Info hotel -->
          <div class="flex justify-between items-center gap-2">
            <div class="flex gap-2 items-center -mt-1 min-w-0 flex-1">
              <div v-if="splitEstrellas(props?.data?.stars?.abbr).countEstrellas" class="shrink-0">
                <UIcon :key="index" v-for="index in splitEstrellas(props?.data?.stars?.abbr).countEstrellas"
                  name="i-heroicons-star-solid" class="text-xs" />
                <span class="text-sm">{{ splitEstrellas(props?.data?.stars?.abbr).rightString }}</span>
              </div>
              <p class="text-xs text-neutral-500 truncate">{{ data?.city }}</p>
            </div>

            <!-- Promos and Tags -->
            <div class="flex items-center gap-1 shrink-0 -mt-2 sm:-mt-4 sm:-mr-2.5">
              <div v-if="isMobile && uniquePromos?.length">
                <UModal v-model:open="openModalPromo">
                  <UBadge @touchend.prevent.stop="openModalPromo = true"
                    :aria-label="$t('strapi.searchPage.filtroPromo')" color="secondary" variant="soft" size="sm"
                    icon="i-lucide-tag" :label="$t('strapi.searchPage.filtroPromo')" />
                  <template #header>
                    <p class="font-bold">{{ $t('strapi.searchPage.filtroPromo') }}</p>
                  </template>
                  <template #body>
                    <p v-for="item in uniquePromos" class="py-2">{{ item }}</p>
                  </template>
                </UModal>
              </div>
              <div v-else v-if="uniquePromos?.length">
                <UTooltip :delay-duration="100" :text="$t('strapi.searchPage.filtroPromo')" :content="{ side: 'top' }"
                  :ui="{ content: 'h-fit py-0' }">
                  <UBadge @touchend.prevent.stop="openModalPromo = true"
                    :aria-label="$t('strapi.searchPage.filtroPromo')" color="secondary" variant="soft" size="sm"
                    icon="i-lucide-tag" :label="$t('strapi.searchPage.filtroPromo')" />
                  <template #content>
                    <div class="p-1 text-xs">
                      <p v-for="item in uniquePromos" class="py-1">{{ item }}</p>
                    </div>
                  </template>
                </UTooltip>
              </div>
              <template v-for="item in data?.tag">

                <!-- Tag Top hotel -->
                <div v-if="item.id === 7" class="relative">
                  <UBadge :label="item.name" color="warning" variant="soft" size="sm" class="shine-effect" />
                  <div class="star">
                    <UIcon name="i-lucide-sparkles" />
                  </div>
                </div>

                <!-- Tag piscina -->
                <div v-else-if="item.id === 25">
                  <div v-if="isMobile">
                    <UModal v-model:open="openModalPiscina">
                      <UBadge @touchend.prevent.stop="openModalPiscina = true" :label="item.name" color="info"
                        variant="soft" size="sm" class="shine-effect hover:cursor-pointer" icon="ic:baseline-pool" />
                      <template #header>
                        <p class="font-bold">{{ item?.name }}</p>
                      </template>
                      <template #body>
                        <p class="py-2">
                          {{ $t('labels.tags.piscina') }}
                        </p>
                      </template>
                    </UModal>
                  </div>
                  <div v-else>
                    <UTooltip :delay-duration="100" :text="$t('labels.tags.piscina')" :content="{ side: 'top' }">
                      <UBadge :label="item.name" color="info" variant="soft" size="sm"
                        class="shine-effect hover:cursor-pointer" icon="ic:baseline-pool" />
                    </UTooltip>
                  </div>
                </div>

                <!-- Tag Spa  -->
                <div v-else-if="item.id === 27">
                  <UBadge :label="item.name" color="info" variant="soft" size="sm"
                    class="shine-effect hover:cursor-pointer" icon="ic:baseline-spa" />
                </div>

                <!-- Tag Amor amistad  -->
                <div v-else-if="item.id === 28">
                  <UBadge :label="item.name" variant="soft" size="sm" class="shine-effect hover:cursor-pointer"
                    icon="i-heroicons-heart-solid" :ui="{
                      leadingIcon: 'text-pink-500',
                      base: 'text-pink-500 bg-pink-500/10'
                    }" />
                </div>

                <!-- Tag normal -->
                <UBadge v-else :label="item.name" color="neutral" size="sm" variant="soft" />
              </template>
            </div>
          </div>

          <p class="text-base sm:text-lg font-bold mb-2 line-clamp-2">{{ data?.name }}</p>

          <!-- Rango horas disponibles -->
          <div class="relative w-full">
            <div class="flex w-full py-2 gap-2 items-center overflow-x-auto scrollbar-hide" ref="horasScroll">
              <template v-for="hourKey in availableHours" :key="hourKey">
                <UTooltip v-if="!isMobile" :delay-duration="100"
                  :text="$t('labels.tooltips.packHours', { hours: hourKey })" :content="{ side: 'top' }">
                  <UButton :label="(() => {
                    const firstHour = arrayHoras?.[hourKey][0].split(':')[0] + (arrayHoras?.[hourKey][0].split(' ')[1] ?? 'h');
                    const lastHour = arrayHoras?.[hourKey][arrayHoras?.[hourKey].length - 1].split(':')[0] + (arrayHoras?.[hourKey][arrayHoras?.[hourKey].length - 1].split(' ')[1] ?? 'h');
                    if (!changeHourFormat(browserLang)) {
                      return firstHour === lastHour ? firstHour : `${firstHour} - ${lastHour}`;
                    } else { // Si la primera cifra es 0, se la quitamos
                      const cleanHour = (h: string) => h.replace(/^0/, '');
                      return cleanHour(firstHour) === cleanHour(lastHour) ? cleanHour(firstHour) : `${cleanHour(firstHour)} - ${cleanHour(lastHour)}`;
                    }
                  })()" color="neutral" size="xs" :ui="{
                    base: 'px-2 py-1.5 shrink-0'
                  }" variant="outline" @click.prevent.stop="() => {
                    navigateTo(`/${locale}/${props?.data?.slug}-${props?.data?.id}.html?${urlParams ?? ''}&pack=${hourKey}`, { open: { target: alternative ? '_self' : '_blank' } })
                  }">
                    <template #leading>
                      <UBadge :label="`${hourKey}h`" color="neutral" size="sm" variant="solid" />
                    </template>
                  </UButton>
                </UTooltip>

                <UButton v-else :label="(() => {
                  const firstHour = arrayHoras?.[hourKey][0].split(':')[0] + (arrayHoras?.[hourKey][0].split(' ')[1] ?? 'h');
                  const lastHour = arrayHoras?.[hourKey][arrayHoras?.[hourKey].length - 1].split(':')[0] + (arrayHoras?.[hourKey][arrayHoras?.[hourKey].length - 1].split(' ')[1] ?? 'h');
                  if (!changeHourFormat(browserLang)) {
                    return firstHour === lastHour ? firstHour : `${firstHour} - ${lastHour}`;
                  } else { // Si la primera cifra es 0, se la quitamos
                    const cleanHour = (h: string) => h.replace(/^0/, '');
                    return cleanHour(firstHour) === cleanHour(lastHour) ? cleanHour(firstHour) : `${cleanHour(firstHour)} - ${cleanHour(lastHour)}`;
                  }
                })()" color="neutral" size="xs" :ui="{
                  base: 'px-2.5 py-2 shrink-0'
                }" variant="outline" @click.prevent.stop="() => {
                  navigateTo(`/${locale}/${props?.data?.slug}-${props?.data?.id}.html?${urlParams ?? ''}&pack=${hourKey}`, { open: { target: alternative ? '_self' : '_blank' } })
                }">
                  <template #leading>
                    <UBadge :label="`${hourKey}h`" color="neutral" size="xs" variant="solid" />
                  </template>
                </UButton>
              </template>
            </div>
          </div>

        </div>

        <!-- Tabla de precios -->
        <div class="mt-3 shrink-0">
          <div class="grid grid-rows-1 grid-flow-col grid-cols-3 gap-2">
            <div v-if="data?.pack_min_price?.[3]?.price_label" class="min-w-0">
              <div class="text-xs sm:text-sm text-neutral-500">3 {{ $t('labels.horas') }}</div>
              <div class="text-base sm:text-lg font-semibold truncate">{{ data?.pack_min_price?.[3]?.price_label }}
              </div>
            </div>
            <div v-if="data?.pack_min_price?.[6]?.price_label" class="min-w-0">
              <div class="text-xs sm:text-sm text-neutral-500">6 {{ $t('labels.horas') }}</div>
              <div class="text-base sm:text-lg font-semibold truncate">{{ data?.pack_min_price?.[6]?.price_label }}
              </div>
            </div>
            <div v-if="data?.pack_min_price?.[24]?.price_label" class="min-w-0">
              <div class="text-xs sm:text-sm text-neutral-500">24 {{ $t('labels.horas') }}</div>
              <div class="text-base sm:text-lg font-semibold truncate">{{ data?.pack_min_price?.[24]?.price_label }}
              </div>
            </div>
          </div>
        </div>
      </NuxtLink>
    </transition>

    <!-- Modales de autenticación -->
    <AuthModals v-model:show-login-modal="showLoginModal" v-model:show-register-modal="showRegisterModal"
      :hotel-id="props?.data?.id" @login-success="() => {
        favoriteHotel(props?.data?.id)
        invalidateSearchCache = true
        reloadNuxtApp()
      }" @register-success="() => {
        favoriteHotel(props?.data?.id)
      }" />
  </div>
</template>

<style scoped>
.shine-effect {
  position: relative;
  overflow: hidden;
}

.shine-effect::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(to right,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent);
  transform: skewX(-25deg);
  animation: shine 15s infinite;
  z-index: 2;
}

.star {
  position: absolute;
  top: -5px;
  right: -1px;
  font-size: 11px;
  color: #fbbf24;
  opacity: 1;
  z-index: 1;
}

@keyframes shine {
  0% {
    left: -100%;
  }

  8% {
    left: 100%;
  }

  18% {
    left: -100%;
  }

  26% {
    left: 100%;
  }

  36% {
    left: -100%;
  }

  44% {
    left: 100%;
  }

  54% {
    left: -100%;
  }

  62% {
    left: 100%;
  }

  100% {
    left: 100%;
  }
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@keyframes heartbeat {

  0%,
  100% {
    transform: scale(1);
  }

  25% {
    transform: scale(1.2);
  }

  50% {
    transform: scale(0.95);
  }

  75% {
    transform: scale(1.1);
  }
}

.heartbeat-animation {
  animation: heartbeat 2s ease-in-out;
}
</style>